
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { label: '기업/대표이사소개', id: 'about' },
    { label: '핵심가치', id: 'core-value' },
    { label: '주요업무', id: 'legal-services' },
    { label: '컨설팅분야', id: 'consulting' },
    { label: '용역수행실적', id: 'performance' },
    { label: '자료실', id: 'resources' },
  ];

  return (
    <section className="relative h-[100svh] min-h-[600px] w-full overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/b12a773401abbc4c9f71d2d915ee98ee.jpg"
          alt="건설 안전 현장"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2b4a]/85 via-[#1a2b4a]/75 to-[#1a2b4a]/85"></div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img
              src="https://public.readdy.ai/ai/img_res/610af156-a17e-4843-b51d-56e6ec41338a.png"
              alt="더불어안전 로고"
              className="h-9 sm:h-12 w-auto"
            />
            <div>
              <div
                className={`text-sm sm:text-lg font-bold ${
                  scrolled ? 'text-[#1a2b4a]' : 'text-white'
                }`}
              >
                (주)더불어안전
              </div>
              <div
                className={`text-[10px] sm:text-xs ${
                  scrolled ? 'text-gray-600' : 'text-white/80'
                }`}
              >
                한창안전기술사사무소
              </div>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors hover:text-[#ff6b35] whitespace-nowrap ${
                  scrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button className="hidden sm:block px-5 sm:px-6 py-2 sm:py-2.5 bg-[#ff6b35] text-white text-xs sm:text-sm font-medium rounded-full hover:bg-[#ff5722] transition-colors whitespace-nowrap">
              상담문의
            </button>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? 'text-[#1a2b4a]' : 'text-white'
              }`}
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white shadow-xl overflow-hidden"
            >
              <div className="px-4 py-4 space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-[#ff6b35]/5 hover:text-[#ff6b35] rounded-lg transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
                <div className="pt-2 px-4">
                  <button className="w-full py-2.5 bg-[#ff6b35] text-white text-sm font-medium rounded-full hover:bg-[#ff5722] transition-colors whitespace-nowrap">
                    상담문의
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="text-white/90 text-2xl sm:text-3xl md:text-5xl font-light mb-2 sm:mb-4">
              국내 최고 수준의
            </div>
            <h1 className="text-white text-4xl sm:text-5xl md:text-7xl font-bold leading-tight mb-2 sm:mb-3">
              건설안전 컨설팅 및
              <span className="block mt-2 sm:mt-3 md:mt-4">재해예방전문지도기관</span>
            </h1>
            <div className="text-[#ff6b35] text-base sm:text-lg md:text-xl font-medium mb-4 sm:mb-6">
              고용노동부 지정 재해예방전문지도기관
            </div>
            <p className="text-white/90 text-sm sm:text-base md:text-lg mb-6 sm:mb-10 leading-relaxed">
              중소규모 건설 재해예방의 선도적 역할 수행<br />
              현장중심의 실효성 있는 재해예방기술지도 및 안전관리 솔루션 제공
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5">
              <button className="px-8 sm:px-12 py-3 sm:py-4 bg-[#ff6b35] text-white text-sm sm:text-base font-semibold rounded-2xl hover:bg-[#ff5722] transition-all hover:shadow-xl whitespace-nowrap">
                컨설팅 문의하기
              </button>
              <button className="px-8 sm:px-12 py-3 sm:py-4 bg-transparent border-2 border-white text-white text-sm sm:text-base font-semibold rounded-2xl hover:bg-white/10 transition-all whitespace-nowrap">
                사업소개서 다운로드
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 cursor-pointer"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        onClick={() => scrollToSection('about')}
      >
        <i className="ri-arrow-down-line text-white text-2xl sm:text-3xl"></i>
      </motion.div>
    </section>
  );
}
